import { scopeFor } from '@ofmos/shared/roles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppClient } from '../../data/ClientProvider';
import type { FieldMetadata, FieldProvenance, FieldValue, RecordEvent } from '../../data/client';
import { queryKeys } from '../../data/queryKeys';
import { formatDateTime } from '../../fields/format';
import { Button, useToast } from '../../ui';
import { useSession } from '../auth/useSession';
import { ReadOnlyFieldValue } from './ReadOnlyFieldValue';

/**
 * THE LOCK, WHAT IT REFUSED, AND THE ONE CONTROL THAT LIFTS IT (D24, question 2).
 *
 * ARIF DECIDED WHERE THIS LIVES, and it is not a settings screen. He wants a machine's
 * blocked write attempts easy to review and approve, on one condition: the old value must
 * stay visible in the history - "Diese Nummer wurde in diese Nummer geaendert" - and he
 * named Close CRM as the experience to match. So the release control sits NEXT TO the
 * value that held and the value that was refused, on the field itself, where a person can
 * see what they are agreeing to before they agree to it.
 *
 * THE ATTEMPT COMES FROM THE EVENT LOG, not from session state. `useRecordFieldSave`'s
 * `refused` marker only exists for a write THIS TAB just made; the interesting case is an
 * import or a workflow that was refused at three in the morning. INV-4 events carry real
 * before/after diffs, and a `write_blocked` event's `before` is the value that held while
 * its `after` is what the machine wanted - which is exactly the pair this renders.
 *
 * PER FIELD, ONE CLICK, AND ITS COST IS THE POINT (D24 item 4). There is no bulk release
 * anywhere in this product: not per run, not per column, not per record. Releasing four
 * hundred locks from a summary screen is the silent overwrite INV-5 exists to prevent,
 * wearing a button.
 *
 * IT DOES NOT RENDER AT ALL for somebody who cannot use it. The route needs a signed-in
 * person - an API key holding `update` could otherwise release a human lock and overwrite
 * the value it had just unlocked - and it needs write access to the object.
 */
export interface LockReleaseNoteProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly field: FieldMetadata;
  readonly provenance: FieldProvenance;
  readonly value: FieldValue;
  /**
   * The newest `write_blocked` event on this field, from the record's loaded timeline.
   * Its `before` is the value that held and its `after` is what the machine wanted.
   */
  readonly refusal: RecordEvent;
}

export function LockReleaseNote({
  objectId,
  recordId,
  field,
  provenance,
  value,
  refusal,
}: LockReleaseNoteProps) {
  const session = useSession();
  const client = useAppClient();
  const queryClient = useQueryClient();
  const toast = useToast();

  const release = useMutation({
    mutationFn: () => client.records.releaseFieldLock(objectId, recordId, field.id),
    onSuccess: (result) => {
      /** The response carries the whole record, so the canonical key is written (INV-13). */
      queryClient.setQueryData(queryKeys.record(objectId, recordId), result.record);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recordEvents(objectId, recordId) });
      toast({
        title: result.changed
          ? `${field.label} can be updated by automated writers again.`
          : `${field.label} was not held by anybody.`,
      });
    },
    onError: (error: Error) => toast({ tone: 'danger', title: error.message }),
  });

  const mayRelease =
    session.data != null && scopeFor(session.data.permissions, objectId, 'update') !== 'none';

  const attempted = refusal.diff.find((entry) => entry.fieldId === field.id)?.after;
  const holder = provenance.displayName ?? provenance.userId;

  return (
    /* WAVE 3 (R-B1): text under the value, not a sunken box inside the group box row. The
       reason is `FieldStatusNotes`': one row does not hold a second framed surface. */
    <div className="mt-1">
      {/**
       * NOT the standing lock sentence: that is the marker's tooltip one row up, and
       * repeating it under every locked field is what drowned this card once already
       * (Arif, 2026-07-28). What this block adds is the pair of values and the control.
       */}
      <p className="text-2xs text-ink-muted">
        An automated writer tried to change this while {holder} was holding it.
      </p>

      {attempted !== undefined && (
        <p className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-ink">
          <span className="text-ink-subtle">Refused on {formatDateTime(refusal.at)}:</span>
          <ReadOnlyFieldValue objectId={objectId} recordId={recordId} field={field} value={value} />
          <span className="text-ink-subtle">would have become</span>
          <ReadOnlyFieldValue
            objectId={objectId}
            recordId={recordId}
            field={field}
            value={attempted}
          />
        </p>
      )}

      {mayRelease && (
        <div className="mt-2">
          <Button
            variant="ghost"
            size="sm"
            loading={release.isPending}
            onClick={() => release.mutate()}
          >
            Release field
          </Button>
        </div>
      )}
    </div>
  );
}
